/**
 * Multiplayer screen: the room you are in (code + shareable `?room=` link),
 * who is connected (ping, host marker), kick (host) and leave. Without a
 * session it offers the private-room path: join by code, or re-host a room
 * this browser cached. The shared world's own Play button lives on the title.
 */

import { GuiStage, Label, McButton } from '../kit';

export interface MpPlayerRow { id: string; name: string; ping: number; isHost: boolean; isMe: boolean }

export interface MpController {
  inSession(): boolean;
  code(): string;
  /** Shareable link that drops the next player into this room. */
  roomUrl(): string;
  statusText(): string;
  isHost(): boolean;
  players(): MpPlayerRow[];
  join(code: string, name: string): void;
  rehost(code: string, name: string): void;
  leave(): void;
  kick(id: string): void;
  cachedRooms(): Promise<Array<{ code: string; savedAt: number }>>;
  transportLabel(): string;
}

/** Room codes may be longer than the old hand-typed 6 (the main room is 6). */
const CODE_RE = /^[A-Z0-9]{3,12}$/;

export class MultiplayerScreen {
  readonly stage: GuiStage;
  open = false;
  onClose: (() => void) | null = null;
  private refreshTimer = 0;
  private error = '';

  constructor(parent: HTMLElement, private readonly guiScale: () => number, private readonly ctl: MpController) {
    this.stage = new GuiStage(parent, 46);
    this.stage.hide();
    window.addEventListener('resize', () => { if (this.open) this.build(); });
  }

  show(): void {
    this.open = true;
    this.error = '';
    this.stage.setGuiScaleSetting(this.guiScale());
    this.stage.show();
    this.build();
    clearInterval(this.refreshTimer);
    this.refreshTimer = window.setInterval(() => { if (this.open && this.ctl.inSession()) this.build(); }, 1000);
  }

  hide(): void {
    if (!this.open) return;
    this.open = false;
    clearInterval(this.refreshTimer);
    this.stage.hide();
    this.stage.content.replaceChildren();
    this.onClose?.();
  }

  handleKey(e: KeyboardEvent): boolean {
    if (e.code === 'Escape') { this.hide(); return true; }
    return false;
  }

  private input(x: number, y: number, w: number, value: string, max: number, placeholder: string): HTMLInputElement {
    const i = document.createElement('input');
    i.value = value;
    i.maxLength = max;
    i.placeholder = placeholder;
    i.spellcheck = false;
    i.autocomplete = 'off';
    i.style.cssText =
      `position:absolute;left:${x}px;top:${y}px;width:${w}px;height:20px;box-sizing:border-box;` +
      'background:#000;color:#e0e0e0;border:1px solid #a0a0a0;font:8px monospace;padding:0 4px;outline:none;';
    i.addEventListener('focus', () => { i.style.borderColor = '#ffffff'; });
    i.addEventListener('blur', () => { i.style.borderColor = '#a0a0a0'; });
    // typing must not reach the game's key handlers
    for (const ev of ['keydown', 'keyup', 'keypress'] as const) i.addEventListener(ev, (e) => {
      e.stopPropagation();
      if (ev === 'keydown' && (e as KeyboardEvent).code === 'Escape') this.hide();
    });
    return i;
  }

  /** Copy text to the clipboard and say so on the button that did it. */
  private copy(text: string, button: McButton): void {
    void navigator.clipboard?.writeText(text).then(
      () => { button.setLabel('Copied'); setTimeout(() => button.setLabel('Copy Link'), 1200); },
      () => { this.error = 'Clipboard blocked: ' + text; this.build(); },
    );
  }

  private build(): void {
    const st = this.stage;
    st.relayout();
    const c = st.content;
    const focused = document.activeElement instanceof HTMLInputElement && c.contains(document.activeElement);
    if (focused) return; // don't rebuild under the user's cursor
    c.replaceChildren();
    const dim = document.createElement('div');
    dim.style.cssText = 'position:absolute;left:0;top:0;width:100%;height:100%;background:rgba(16,16,16,0.82);';
    c.appendChild(dim);
    const W = st.width, H = st.height;
    const cx = Math.floor(W / 2);
    c.appendChild(new Label('Multiplayer', cx, 12, { align: 'center', color: '#ffffff', shadow: true }).el);
    const ctl = this.ctl;

    if (!ctl.inSession()) {
      let y = 40;
      c.appendChild(new Label("Not in a room yet - press Play on the title,", cx, y, { align: 'center', color: '#a0a0a0' }).el);
      c.appendChild(new Label("or join a friend's private room:", cx, y + 10, { align: 'center', color: '#a0a0a0' }).el);
      y += 30;
      c.appendChild(new Label('Room code', cx - 100, y, { color: '#a0a0a0' }).el);
      const code = this.input(cx - 100, y + 10, 96, '', 12, 'MAINV3');
      code.style.textTransform = 'uppercase';
      c.appendChild(code);
      const doJoin = (): void => {
        const v = code.value.trim().toUpperCase();
        if (!CODE_RE.test(v)) { this.error = 'Room codes are letters and digits'; this.build(); return; }
        ctl.join(v, '');
      };
      code.addEventListener('keydown', (e) => { if (e.code === 'Enter') doJoin(); });
      c.appendChild(new McButton('Join', cx, y + 10, 100, 20, doJoin).el);
      y += 44;
      const rooms = document.createElement('div');
      c.appendChild(rooms);
      void ctl.cachedRooms().then((list) => {
        if (!this.open || ctl.inSession() || list.length === 0) return;
        rooms.appendChild(new Label('Rooms cached in this browser (re-host):', cx - 100, y, { color: '#a0a0a0' }).el);
        list.slice(0, 3).forEach((r, i) => {
          const when = new Date(r.savedAt).toLocaleDateString();
          rooms.appendChild(new McButton(`${r.code}  (${when})`, cx - 100, y + 10 + i * 22, 200, 20, () => ctl.rehost(r.code, '')).el);
        });
      });
    } else {
      c.appendChild(new Label(`Room code: ${ctl.code()}`, cx, 30, { align: 'center', color: '#ffff55', shadow: true }).el);
      c.appendChild(new Label(ctl.statusText(), cx, 42, { align: 'center', color: '#a0a0a0' }).el);
      const players = ctl.players();
      let y = 60;
      for (const p of players) {
        const tag = (p.isHost ? '[Host] ' : '') + p.name + (p.isMe ? ' (you)' : '');
        c.appendChild(new Label(tag, cx - 110, y + 6, { color: p.isHost ? '#ffd700' : '#ffffff', shadow: true }).el);
        c.appendChild(new Label(p.isMe && ctl.isHost() ? '-' : `${Math.round(p.ping)} ms`, cx + 70, y + 6, { align: 'right', color: pingColor(p.ping) }).el);
        if (ctl.isHost() && !p.isMe) c.appendChild(new McButton('Kick', cx + 76, y, 36, 20, () => { ctl.kick(p.id); this.build(); }).el);
        y += 22;
        if (y > H - 80) break;
      }
      const copyBtn = new McButton('Copy Link', cx - 100, H - 52, 98, 20, () => this.copy(ctl.roomUrl(), copyBtn));
      c.appendChild(copyBtn.el);
      c.appendChild(new McButton('Leave', cx + 2, H - 52, 98, 20, () => { ctl.leave(); this.build(); }).el);
      c.appendChild(new Label(`Transport: ${ctl.transportLabel()}  -  T: chat, Tab: players`, cx, H - 64, { align: 'center', color: '#707070' }).el);
    }
    if (this.error) c.appendChild(new Label(this.error, cx, H - 76, { align: 'center', color: '#ff5555' }).el);
    c.appendChild(new McButton('Done', cx - 100, H - 28, 200, 20, () => this.hide()).el);
  }
}

function pingColor(ms: number): string {
  return ms < 80 ? '#55ff55' : ms < 200 ? '#ffff55' : '#ff5555';
}
